export const DOCK_YOUTUBE_MIN_VIEWPORT_PX = 200
export const DOCK_YOUTUBE_PRELOAD_SYNC_INTERVAL_MS = 3000
// Matches the reveal-time resume tolerance: a preload reported ready must not
// need a catch-up seek at the moment the dock becomes visible.
export const DOCK_YOUTUBE_PRELOAD_SYNC_DRIFT_SECONDS = 0.75
export const DOCK_YOUTUBE_PLAYER_STATE_PLAYING = 1
export const DOCK_YOUTUBE_PLAYER_STATE_BUFFERING = 3

/**
 * @typedef {Object} TreechatYouTubePlayerVars
 * @property {0} controls
 * @property {0 | 1} disablekb
 * @property {0 | 1} enablejsapi
 * @property {0 | 1} fs
 * @property {1 | 3} iv_load_policy
 * @property {string} origin
 * @property {0 | 1} playsinline
 * @property {0 | 1} rel
 */

/**
 * @typedef {TreechatYouTubePlayerVars & {start?: number}} InlineYouTubePlayerVars
 */

export function resolveYouTubePlayerOrigin() {
    const origin =
        typeof window !== 'undefined' && window.location?.origin ? window.location.origin : null
    if (origin && /^https?:\/\//i.test(origin)) {
        return origin
    }

    return 'https://knovigator.com'
}

/**
 * @param {string} [origin]
 * @returns {TreechatYouTubePlayerVars}
 */
function buildTreechatYouTubePlayerVars(origin = resolveYouTubePlayerOrigin()) {
    return {
        controls: 0,
        disablekb: 1,
        enablejsapi: 1,
        fs: 0,
        iv_load_policy: 3,
        origin,
        playsinline: 1,
        rel: 0,
    }
}

const youtubeCaptionRuntimeByPlayer = new WeakMap()

const YOUTUBE_CAPTIONS_MODULE = 'captions'
const YOUTUBE_CAPTIONS_PROBE_TIMEOUT_MS = 1500

const isYouTubePlayerObject = player =>
    Boolean(player) && (typeof player === 'object' || typeof player === 'function')

export function disableYouTubeCaptions(player, videoId = null) {
    if (!isYouTubePlayerObject(player)) return false

    const runtime = getYouTubeCaptionRuntime(player)
    const normalizedVideoId = videoId == null ? null : String(videoId)
    if (runtime.initialized && runtime.videoId === normalizedVideoId) return true

    clearYouTubeCaptionsProbe(player, runtime)
    runtime.availability = 'unknown'
    runtime.requested = false
    runtime.initialized = true
    runtime.videoId = normalizedVideoId
    const unloadedImmediately = unloadYouTubeCaptions(player)
    const probing = startYouTubeCaptionsProbe(player)
    notifyYouTubeCaptionsState(player)
    if (probing || unloadedImmediately) return true

    return false
}

function readYouTubeCaptionOption(player, option) {
    if (typeof player.getOption !== 'function') return null
    try {
        return player.getOption(YOUTUBE_CAPTIONS_MODULE, option) ?? null
    } catch {
        return null
    }
}

function isYouTubeCaptionsModuleLoaded(player) {
    if (typeof player.getOptions !== 'function') return null
    try {
        const modules = player.getOptions()
        return Array.isArray(modules) ? modules.includes(YOUTUBE_CAPTIONS_MODULE) : null
    } catch {
        return null
    }
}

function getYouTubeCaptionRuntime(player) {
    let runtime = youtubeCaptionRuntimeByPlayer.get(player)
    if (!runtime) {
        runtime = {
            availability: 'unknown',
            requested: false,
            initialized: false,
            videoId: null,
            probing: false,
            probeListener: null,
            probeTimeout: null,
            listeners: new Set(),
        }
        youtubeCaptionRuntimeByPlayer.set(player, runtime)
    }
    return runtime
}

function unloadYouTubeCaptions(player) {
    if (typeof player.unloadModule !== 'function') return false
    try {
        player.unloadModule(YOUTUBE_CAPTIONS_MODULE)
        return true
    } catch {
        return false
    }
}

function inspectLoadedYouTubeCaptions(player) {
    if (isYouTubeCaptionsModuleLoaded(player) !== true) return null

    const track = readYouTubeCaptionOption(player, 'track')
    const tracklist = readYouTubeCaptionOption(player, 'tracklist')
    const hasTrack = Boolean(track && typeof track.languageCode === 'string')
    const hasTracklist = Array.isArray(tracklist)
    if (!hasTrack && !hasTracklist) return null

    return {
        available:
            hasTrack || tracklist.some(item => item && typeof item.languageCode === 'string'),
        enabled: hasTrack,
    }
}

function clearYouTubeCaptionsProbe(player, runtime) {
    runtime.probing = false
    if (runtime.probeListener) {
        try {
            player.removeEventListener?.('onApiChange', runtime.probeListener)
        } catch {}
        runtime.probeListener = null
    }
    if (runtime.probeTimeout != null) {
        globalThis.clearTimeout?.(runtime.probeTimeout)
        runtime.probeTimeout = null
    }
}

function settleYouTubeCaptionsProbe(player, { timedOut = false } = {}) {
    const runtime = getYouTubeCaptionRuntime(player)
    const inspected = inspectLoadedYouTubeCaptions(player)
    if (!inspected && !timedOut) return false

    if (!inspected && timedOut) {
        // Stop waiting for the initial answer, but keep the one-shot API
        // listener: a slow module can still arrive while a player is paused.
        // Its eventual settlement still respects the captions-off request.
        runtime.probing = false
        if (runtime.probeTimeout != null) {
            globalThis.clearTimeout?.(runtime.probeTimeout)
            runtime.probeTimeout = null
        }
        if (runtime.availability !== 'available') runtime.availability = 'unavailable'
        runtime.requested = false
        notifyYouTubeCaptionsState(player)
        return true
    }

    clearYouTubeCaptionsProbe(player, runtime)
    if (!inspected.available) {
        runtime.availability = 'unavailable'
        runtime.requested = false
        unloadYouTubeCaptions(player)
        notifyYouTubeCaptionsState(player)
        return true
    }

    runtime.availability = 'available'
    if (!runtime.requested) {
        if (!unloadYouTubeCaptions(player)) runtime.requested = inspected.enabled
        notifyYouTubeCaptionsState(player)
        return true
    }

    ensureYouTubeCaptionTrack(player)
    const enabled = inspectLoadedYouTubeCaptions(player)?.enabled === true
    if (!enabled) runtime.requested = false
    notifyYouTubeCaptionsState(player)
    return true
}

function startYouTubeCaptionsProbe(player) {
    if (typeof player.loadModule !== 'function') return false
    const runtime = getYouTubeCaptionRuntime(player)
    if (runtime.probing) return true
    // A timed-out probe deliberately leaves a one-shot observer behind so a
    // late module can still publish capability. A viewer retry must replace
    // that observer and issue a fresh load request rather than looking active
    // forever.
    if (runtime.probeListener) clearYouTubeCaptionsProbe(player, runtime)
    runtime.probing = true

    const handleApiChange = () => settleYouTubeCaptionsProbe(player)
    if (typeof player.addEventListener === 'function') {
        try {
            player.addEventListener('onApiChange', handleApiChange)
            runtime.probeListener = handleApiChange
        } catch {}
    }

    try {
        player.loadModule(YOUTUBE_CAPTIONS_MODULE)
    } catch {
        clearYouTubeCaptionsProbe(player, runtime)
        return false
    }

    // Some test doubles and already-loaded players expose the module
    // synchronously and never emit another API-change event.
    settleYouTubeCaptionsProbe(player)
    if (runtime.probing && typeof globalThis.setTimeout === 'function') {
        runtime.probeTimeout = globalThis.setTimeout(
            () => settleYouTubeCaptionsProbe(player, { timedOut: true }),
            YOUTUBE_CAPTIONS_PROBE_TIMEOUT_MS,
        )
        runtime.probeTimeout?.unref?.()
    }
    return true
}

function languageBase(language) {
    return String(language || '')
        .toLowerCase()
        .split('-')[0]
}

/**
 * The track the CC toggle shows on a YouTube player: the viewer's language
 * when the video has it, else the first the video offers.
 */
export function pickYouTubeCaptionTrack(
    tracklist,
    language = typeof navigator !== 'undefined' ? navigator.language : null,
) {
    const tracks = Array.isArray(tracklist)
        ? tracklist.filter(track => track && typeof track.languageCode === 'string')
        : []
    if (!tracks.length) return null

    const base = languageBase(language)
    const inLanguage = base ? tracks.find(track => languageBase(track.languageCode) === base) : null
    return inLanguage || tracks[0]
}

// A loaded captions module does not always display a track on its own — a
// viewer whose YouTube preference has captions off gets the module and no
// text. Choosing a track makes the request unambiguous.
function ensureYouTubeCaptionTrack(player) {
    const current = readYouTubeCaptionOption(player, 'track')
    if (current && typeof current.languageCode === 'string') return true

    const track = pickYouTubeCaptionTrack(readYouTubeCaptionOption(player, 'tracklist'))
    if (!track || typeof player.setOption !== 'function') return false

    try {
        player.setOption(YOUTUBE_CAPTIONS_MODULE, 'track', { languageCode: track.languageCode })
        return true
    } catch {
        return false
    }
}

/**
 * What the CC toggle shows for a YouTube player. Availability is unknown
 * while the captions module probe is pending; only an actual track or
 * tracklist makes the control visible. Enabled state always comes from the
 * module's displayed track, never merely from a request to load it.
 *
 * @returns {{ available: boolean, enabled: boolean }}
 */
export function readYouTubeCaptionsState(player) {
    if (!isYouTubePlayerObject(player)) return { available: false, enabled: false }

    const runtime = getYouTubeCaptionRuntime(player)
    const inspected = inspectLoadedYouTubeCaptions(player)
    if (inspected) {
        runtime.availability = inspected.available ? 'available' : 'unavailable'
        if (!inspected.available) {
            runtime.requested = false
            unloadYouTubeCaptions(player)
            return inspected
        }
        // A module that finishes loading after the bounded probe must still
        // respect the player's captions-off initialization (or a later off
        // click), rather than reviving YouTube's own remembered preference.
        if (!runtime.requested && runtime.initialized) {
            if (!unloadYouTubeCaptions(player)) {
                runtime.requested = inspected.enabled
                return inspected
            }
            return { available: true, enabled: false }
        }
        return inspected
    }

    return {
        available: runtime.availability === 'available',
        enabled: false,
    }
}

function notifyYouTubeCaptionsState(player) {
    const runtime = getYouTubeCaptionRuntime(player)
    if (!runtime.listeners.size) return

    const captions = readYouTubeCaptionsState(player)
    for (const listener of [...runtime.listeners]) {
        try {
            listener(captions)
        } catch {}
    }
}

export function subscribeToYouTubeCaptionsState(player, listener) {
    if (!isYouTubePlayerObject(player) || typeof listener !== 'function') return () => {}

    const runtime = getYouTubeCaptionRuntime(player)
    runtime.listeners.add(listener)
    listener(readYouTubeCaptionsState(player))
    return () => runtime.listeners.delete(listener)
}

/**
 * Turns a YouTube player's captions on (load the module, then pick a track
 * once it is up) or off (unload the module). Returns whether the player
 * accepted the request.
 */
export function setYouTubeCaptionsEnabled(player, enabled) {
    if (!isYouTubePlayerObject(player)) return false
    const runtime = getYouTubeCaptionRuntime(player)

    if (!enabled) {
        const previousRequested = runtime.requested
        const inspected = inspectLoadedYouTubeCaptions(player)
        const accepted = unloadYouTubeCaptions(player)
        runtime.requested = accepted ? false : inspected?.enabled ?? previousRequested
        notifyYouTubeCaptionsState(player)
        return accepted
    }

    runtime.requested = true
    if (inspectLoadedYouTubeCaptions(player)?.available) {
        ensureYouTubeCaptionTrack(player)
        if (inspectLoadedYouTubeCaptions(player)?.enabled) {
            notifyYouTubeCaptionsState(player)
            return true
        }
    }

    if (!startYouTubeCaptionsProbe(player)) {
        runtime.requested = false
        notifyYouTubeCaptionsState(player)
        return false
    }
    // A module can settle synchronously during loadModule. If it proves that
    // no track can be enabled (including setOption failure), do not tell the
    // caller to render an optimistic checked state over that result.
    if (!runtime.probing && !runtime.requested) {
        notifyYouTubeCaptionsState(player)
        return false
    }
    notifyYouTubeCaptionsState(player)
    return true
}

/**
 * A player that just came up applies the track's remembered caption choice:
 * the dock's runtime after a takeover, the post's player after it gets the
 * picture back. Captions stay off otherwise (see disableYouTubeCaptions).
 */
export function restoreYouTubeCaptions(player, playbackState) {
    if (playbackState?.captions !== true) return false
    return setYouTubeCaptionsEnabled(player, true)
}

function normalizeYouTubeStart(value) {
    const numericValue = Number(value)
    return Number.isFinite(numericValue) && numericValue > 0.05 ? Math.floor(numericValue) : null
}

export function buildDockYouTubeCueRequest(videoId, start = null) {
    const normalizedVideoId = videoId ? String(videoId) : null
    if (!normalizedVideoId) return null

    const normalizedStart = normalizeYouTubeStart(start)
    return {
        videoId: normalizedVideoId,
        ...(normalizedStart != null ? { startSeconds: normalizedStart } : {}),
    }
}

export function cueDockYouTubePlayer(player, videoId, start = null) {
    const request = buildDockYouTubeCueRequest(videoId, start)
    if (!player || !request || typeof player.cueVideoById !== 'function') return false

    try {
        player.cueVideoById(request)
        return true
    } catch {
        return false
    }
}

export function loadDockYouTubePlayer(player, videoId, start = null) {
    const request = buildDockYouTubeCueRequest(videoId, start)
    if (!player || !request || typeof player.loadVideoById !== 'function') return false

    try {
        player.loadVideoById(request)
        return true
    } catch {
        return false
    }
}

export function muteDockYouTubePlayer(player) {
    if (!player || typeof player.mute !== 'function') return false

    try {
        player.mute()
        return true
    } catch {
        return false
    }
}

export function isDockYouTubePlayerPlaying(player) {
    if (!player || typeof player.getPlayerState !== 'function') return false

    try {
        return player.getPlayerState() === DOCK_YOUTUBE_PLAYER_STATE_PLAYING
    } catch {
        return false
    }
}

export function ensureDockYouTubePreloadPlaying(player) {
    if (!player) return false

    muteDockYouTubePlayer(player)
    if (isDockYouTubePlayerPlaying(player)) return true

    try {
        player.playVideo?.()
        return true
    } catch {
        return false
    }
}

export function primeDockYouTubePreloadPlayer(player, videoId, start = null) {
    if (!player) return false

    muteDockYouTubePlayer(player)
    if (loadDockYouTubePlayer(player, videoId, start)) return true
    if (!cueDockYouTubePlayer(player, videoId, start)) return false

    try {
        player.playVideo?.()
    } catch {}
    return true
}

export function claimDockYouTubePlaybackIfPlaying(player, ownerId, options = {}) {
    if (!isDockYouTubePlayerPlaying(player)) return false

    const { persistPlayback, claimPlayback } = options
    persistPlayback?.(player, { hasStartedPlaying: true })
    // A refused claim (e.g. a stale handoff token) is not a successful
    // takeover — callers must keep waiting rather than mark the handoff done.
    if (claimPlayback && claimPlayback(ownerId) === false) return false
    return true
}

export function shouldClaimInlineYouTubePlayback(playerState, options = {}) {
    if (
        playerState !== DOCK_YOUTUBE_PLAYER_STATE_PLAYING &&
        playerState !== DOCK_YOUTUBE_PLAYER_STATE_BUFFERING
    ) {
        return false
    }
    if (options.isHandoffSource) return false

    // Normal inline buffering still counts as active playback. During an
    // inline-to-inline handoff, however, the destination must wait for a real
    // PLAYING event or it will pause the source into a visible buffer gap.
    return !(playerState === DOCK_YOUTUBE_PLAYER_STATE_BUFFERING && options.isHandoffDestination)
}

export function shouldWaitForDockYouTubePreload(options = {}) {
    const { track, deferInitialOwnerPause, readyMediaKey } = options
    return Boolean(
        track?.kind === 'youtube' &&
            track.mediaKey &&
            deferInitialOwnerPause === true &&
            readyMediaKey !== track.mediaKey,
    )
}

/**
 * @param {string | {origin?: string, start?: string | number | null}} [options]
 * @returns {TreechatYouTubePlayerVars & {start?: number}}
 */
export function buildDockYouTubePlayerVars(options = {}) {
    const normalizedOptions =
        typeof options === 'string'
            ? { origin: options }
            : options && typeof options === 'object'
            ? options
            : {}
    const normalizedStart = normalizeYouTubeStart(normalizedOptions.start)

    return {
        ...buildTreechatYouTubePlayerVars(normalizedOptions.origin || resolveYouTubePlayerOrigin()),
        ...(normalizedStart != null ? { start: normalizedStart } : {}),
    }
}

/**
 * @param {{start?: string | number | null, origin?: string}} options
 * @returns {InlineYouTubePlayerVars}
 */
export function buildInlineYouTubePlayerVars(options = {}) {
    const { start = null, origin = resolveYouTubePlayerOrigin() } = options
    const numericStart = Number(start)

    return {
        ...buildTreechatYouTubePlayerVars(origin),
        ...(start != null && start !== '' && Number.isFinite(numericStart)
            ? { start: numericStart }
            : {}),
    }
}

export function prepareDockYouTubeIframe(iframe) {
    if (!iframe) return null

    try {
        iframe.setAttribute?.('tabindex', '-1')
        iframe.setAttribute?.('scrolling', 'no')
        iframe.setAttribute?.('data-treechat-dock-youtube', 'true')
    } catch {}

    try {
        if (iframe.style) iframe.style.pointerEvents = 'none'
    } catch {}

    return iframe
}
